package com.evoapps.evoshift.filedrop

import com.evoapps.evoshift.ui.util.UiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.file_drop_only_one
import evoshift.composeapp.generated.resources.file_drop_skipped
import evoshift.composeapp.generated.resources.file_drop_wrong_type
import evoshift.composeapp.generated.resources.file_drop_wrong_types

/** A file dropped on the app: its name and its bytes (at most [FileDropSpec.readLimit] of them). */
class DroppedFile(val name: String, val bytes: ByteArray)

/**
 * One file of a drop before it is read: its name, its type when the platform knows it (a file
 * from a photo library may have no extension), and how to read it: [read] reads at most `limit`
 * bytes and calls `done` once, with null when the file could not be read.
 */
class DropCandidate(
    val name: String,
    val mimeType: String? = null,
    val read: (limit: Int, done: (ByteArray?) -> Unit) -> Unit
)

/**
 * SH-233: what one upload place takes when a file is dropped on it, the same as its picker: the
 * file name [extensions] (lower case) and [mimeTypes] ("image/" followed by a star for any image) the chooser
 * offers, the [readLimit] it reads (one byte over the place's size limit, so the place's own
 * "too large" message follows), and whether it takes several files at once ([multiple]).
 */
data class FileDropSpec(
    val extensions: Set<String>,
    val readLimit: Int,
    val mimeTypes: Set<String> = emptySet(),
    val multiple: Boolean = false
) {
    /** Whether a file of this [name] (and [mimeType], when known) fits here. */
    fun accepts(name: String, mimeType: String? = null): Boolean {
        val extension = name.substringAfterLast('.', "").lowercase()
        if (extension.isNotEmpty() && extension in extensions) return true
        val type = mimeType?.lowercase()?.takeIf { it.isNotBlank() } ?: return false
        return mimeTypes.any { accepted -> if (accepted.endsWith("/*")) type.startsWith(accepted.dropLast(1)) else type == accepted }
    }

    /** The kinds of file that fit, as people know them ("PDF, JPG, PNG"). */
    val kinds: String
        get() = extensions.filterNot { it == "jpeg" && "jpg" in extensions }.joinToString(", ") { it.uppercase() }

    /**
     * What to do with a drop of files named [names] (and typed [mimeTypes], index by index):
     * null when nothing was dropped.
     */
    fun decide(names: List<String>, mimeTypes: List<String?> = emptyList()): DropDecision? {
        if (names.isEmpty()) return null
        val fitting = names.indices.filter { accepts(names[it], mimeTypes.getOrNull(it)) }
        val refused = names.indices.filter { it !in fitting }.map { names[it] }
        if (fitting.isEmpty()) {
            val text = if (refused.size == 1) Res.string.file_drop_wrong_type else Res.string.file_drop_wrong_types
            return DropDecision.Refuse(UiText.Resource(text, refused.joinToString(", "), kinds))
        }
        if (!multiple) {
            val taken = fitting.first()
            val notice = if (names.size > 1) UiText.Resource(Res.string.file_drop_only_one, names[taken]) else null
            return DropDecision.Take(listOf(taken), notice)
        }
        val notice = if (refused.isNotEmpty()) UiText.Resource(Res.string.file_drop_skipped, refused.joinToString(", "), kinds) else null
        return DropDecision.Take(fitting, notice)
    }
}

/** What a drop comes to ([FileDropSpec.decide]). */
sealed interface DropDecision {
    /** Read the files at [indices] and hand them over; tell the person [notice] (files left out), if any. */
    data class Take(val indices: List<Int>, val notice: UiText?) : DropDecision

    /** Nothing dropped fits here: tell the person [message]. */
    data class Refuse(val message: UiText) : DropDecision
}

/**
 * SH-233: takes a drop for one place: decides with [spec], reads the files taken and hands them
 * to [onFiles] in the order dropped, once all are read (a file that could not be read is null,
 * as from the picker). [onMessage] gets what the person is told: why nothing was taken, which
 * files were left out, or null when all went in.
 */
class FileDropHandler(
    var spec: FileDropSpec,
    var onFiles: (List<DroppedFile?>) -> Unit,
    var onMessage: (UiText?) -> Unit
) {
    /** Handles a drop of [candidates]; false when there was nothing to take. */
    fun drop(candidates: List<DropCandidate>): Boolean {
        val decision = spec.decide(candidates.map { it.name }, candidates.map { it.mimeType }) ?: return false
        when (decision) {
            is DropDecision.Refuse -> onMessage(decision.message)
            is DropDecision.Take -> {
                onMessage(decision.notice)
                val results = arrayOfNulls<DroppedFile>(decision.indices.size)
                val done = BooleanArray(decision.indices.size)
                var left = decision.indices.size
                val deliver = onFiles
                decision.indices.forEachIndexed { slot, index ->
                    val candidate = candidates[index]
                    candidate.read(spec.readLimit) { bytes ->
                        if (!done[slot]) {
                            done[slot] = true
                            results[slot] = bytes?.let { DroppedFile(candidate.name, it) }
                            left--
                            if (left == 0) deliver(results.toList())
                        }
                    }
                }
            }
        }
        return true
    }
}
